// The food-name search query, in one place for the app and the server (`buildFoodNameSearchQuery`
// below) and for the replay tool that measures its ranking (`appNameSearchBody` in
// `tools/src/replay-food-search-shared.ts`). Both build their request from
// `buildFoodNameSearchQueryBody`, so a ranking change made here reaches both (df-254d80d1bbff).

/**
 * Whether the name search forgives a mistyped letter (#5579, food staples plan 4b).
 *
 * `OneLetter` asks Elasticsearch for `fuzziness: "AUTO"`, the usual behaviour. `None` is for a line
 * the person picked from the suggestions: it was built from real food names, so the tolerance only
 * adds near-miss foods ("great value" also finding "groats"). Stemming is the analyzer's job and
 * stays either way, so plurals still match.
 */
export enum TypoTolerance {
  OneLetter = 'OneLetter',
  None = 'None',
}

/**
 * The `query` part of the request that finds foods whose name or alternative names match what was
 * typed, ranked by the match plus each food's stored `score_boost`. The callers add what differs
 * between them: how many hits to return, and which fields to return.
 */
export function buildFoodNameSearchQueryBody(searchText: string, typoTolerance: TypoTolerance): object {
  // if the tolerance is off, the fuzziness setting is left out altogether rather than set to 0
  const fuzziness = typoTolerance === TypoTolerance.OneLetter ? { fuzziness: "AUTO" } : {};
  return {
    script_score: {
      query: {
        bool: {
          should: [
            {
              match: {
                name: {
                  query: searchText,
                  ...fuzziness,
                }
              }
            },
            {
              match_phrase: {
                name: {
                  query: searchText,
                  boost: 2.0,
                  slop: 2
                }
              }
            },
            {
              match: {
                altNames: {
                  query: searchText,
                  ...fuzziness,
                  boost: 0.8,
                }
              }
            },
            {
              match_phrase: {
                altNames: {
                  query: searchText,
                  boost: 1.6,
                  slop: 2
                }
              }
            },
          ]
        }
      },
      script: {
        source: `
          // fallback to 0 if score_boost is missing
          double boost = doc['score_boost'].size() == 0 ? 0 : doc['score_boost'].value;
          
          // boost if no EANs are present
          int boostEan = doc['EANs'].size() == 0 ? 5 : 0;
          
          // bias the final score to prevent negative values
          double totalScore = _score + boost * 4 + 1000 + boostEan;
          return Math.max(0, totalScore);
        `
      }
    }
  };
}

/** Builds the query that finds foods whose name or alternative names match what was typed. */
export function buildFoodNameSearchQuery(searchText: string, maxCount: number, typoTolerance: TypoTolerance = TypoTolerance.OneLetter): string {
  return JSON.stringify({
    size: maxCount,
    query: buildFoodNameSearchQueryBody(searchText, typoTolerance),
  });
}
